import React, { useContext, useEffect, useRef, useState } from 'react';

// third-party libraries
import {
  Button,
  Form,
  Input,
  Popconfirm,
  Table,
  Select,
  DatePicker,
  notification,
} from 'antd';
import moment from 'moment';
import { DeleteOutlined } from '@ant-design/icons';
import { useMutation } from 'react-query';

// API client
import api from 'api';

// utils
import { checkStatusAndHighlight } from '../../utils';

// styles
import './index.less';

const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(true);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  const save = async () => {
    try {
      const values = await form.validateFields();
      handleSave({
        ...record,
        ...values,
      });
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  const renderField = () => {
    switch (dataIndex) {
      case 'product_name':
      case 'manufacturers_name':
        return (
          <Select
            size="large"
            style={{ width: '100%' }}
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
          >
            <Select.Option value={'all'}>All</Select.Option>
          </Select>
        );
      case 'lot_number':
        return (
          <Input
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            size="large"
          />
        );
      case 'expiration_date':
      case 'reminder_date':
        return (
          <DatePicker
            format={'MM/DD/YYYY'}
            size="large"
            style={{ width: '100%' }}
            onBlur={save}
            onOpenChange={(open) => !open && save()}
          />
        );

      default:
        break;
    }
  };

  let childNode = children;
  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} is required.`,
          },
        ]}
        initialValue={
          dataIndex?.includes('date')
            ? moment(record[dataIndex])
            : record[dataIndex]
        }
      >
        {renderField()}
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
      >
        {children}
      </div>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

const VaccinationReminderTable = ({ tableData, setTableData }) => {
  const deleVaccinationMutation = useMutation(
    (id) => api.vaccination.deleteVaccination(id),
    {
      onError: (error) => {
        notification.error({
          message: 'Delete Vaccination Error',
          description: `${error.response.data.message}`,
        });
      },
    },
  );

  const handleDelete = (key, id) => {
    const newData = tableData.filter(
      (item) => item?.key != key || item?.id != id,
    );
    id && deleVaccinationMutation.mutate(id);

    setTableData(newData);
  };

  const defaultColumns = [
    {
      title: 'Vaccine Status',
      dataIndex: 'vaccine_status',
      render(_, record) {
        const date = moment(record?.expiration_date)?.format('MM/DD/YYYY');

        const status = checkStatusAndHighlight(date);

        const isCurrent =
          (!status.isOneDayAhead && !status.isOneDayBehind) ||
          status.isOneDayAhead;

        return (
          <>
            <span
              className={` vaccine-status ${isCurrent ? 'current' : 'past'}`}
            >
              {isCurrent ? 'Current' : 'Past Due'}, {date}
            </span>
          </>
        );
      },
    },
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      editable: true,
    },
    {
      title: 'Manufacturer’s Name',
      dataIndex: 'manufacturers_name',
      editable: true,
    },
    {
      title: 'Lot Number',
      dataIndex: 'lot_number',
      editable: true,
    },
    {
      title: 'Expiration Date',
      dataIndex: 'expiration_date',
      editable: true,
    },
    {
      title: 'Reminder Date',
      dataIndex: 'reminder_date',
      editable: true,
    },
    {
      title: '',
      dataIndex: 'operation',
      render: (_, record) =>
        tableData.length >= 1 ? (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => handleDelete(record.key, record?.id)}
          >
            <Button type="link" danger>
              <DeleteOutlined /> Delete
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  const handleSave = (row) => {
    const newData = [...tableData];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setTableData(newData);
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <div>
      <Table
        components={components}
        rowClassName={() => 'editable-row'}
        dataSource={tableData}
        columns={columns}
        pagination={{ position: ['none', 'none'], pageSize: 200 }}
        loading={deleVaccinationMutation.isLoading}
      />
    </div>
  );
};
export default VaccinationReminderTable;
